import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { MessageCircle, MapPin, Send, CheckCircle2, XCircle, Clock, PackageCheck, HandCoins, Loader2, Bell, BellOff, Pencil, Trash2 } from "lucide-react";
import { TYPE_META } from "@/pages/MarketplacePage";
import { useMarketplaceUnread } from "@/lib/marketplaceUnread";
import { ReportContentButton } from "@/components/ReportContentDialog";
import { BlockUserButton } from "@/components/BlockUserButton";
import { CommentLikeButton, CommentSortSelect } from "@/components/CommentSocial";
import { ChatRequestButton } from "@/components/ChatRequestDialog";
import VideoGallery from "@/components/VideoGallery";
import ShareButton from "@/components/ShareButton";
import { displayAuthorName } from "@/lib/anonAuthor";
import { useMarketplaceRulesGate } from "@/components/MarketplaceRulesGate";

// A minimal chat with sellers/buyers using existing reports chat pattern via
// listing-scoped rooms. We reuse a simple messages endpoint per listing.
export default function MarketplaceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const { refresh: refreshUnread, perListing: mkUnreadPerListing } = useMarketplaceUnread();
  const [l, setL] = useState(null);
  const [threads, setThreads] = useState([]);           // for seller: list of participant users
  const [activePeerId, setActivePeerId] = useState(null); // for seller: currently viewed thread
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectBuyerOpen, setSelectBuyerOpen] = useState(false);
  // Lotto 12 — owner edit dialog + delete confirmation.
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", description: "", price: "", condition: "", color: "", keywords: "", pickup: true, shipping: false, negotiable: false });
  // Interested user must explicitly click "Contatta il venditore/utente" before
  // a private conversation is opened. If the user already has messages in the
  // thread, we auto-open it so they land straight on their history.
  const [chatOpen, setChatOpen] = useState(false);
  // iter80: adult Marketplace Rules gate. Must be called BEFORE any conditional
  // return statements, per Rules of Hooks.
  const { requireMarketplaceRules, gateElement } = useMarketplaceRulesGate();

  const load = async () => {
    try {
      const { data } = await api.get(`/marketplace/listings/${id}`);
      setL(data);
    } catch (e) {
      if (e.response?.status === 401) { nav("/auth?mode=signin"); return; }
      toast.error(e.response?.data?.detail || "Errore");
    }
  };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [id]);

  const loadMessages = async (peerId) => {
    try {
      const { data } = await api.get(`/marketplace/listings/${id}/messages`, { params: peerId ? { peer_id: peerId } : {} });
      const msgs = data.messages || [];
      setMessages(msgs);
      if (data.participants) setThreads(data.participants);
      // Auto-open the chat panel for interested users who already have
      // existing messages so they don't have to click «Contatta» again.
      if (!isOwnerCheck() && msgs.length > 0) setChatOpen(true);
      // Backend auto-marks messages as read on GET; refresh the header badge.
      refreshUnread();
    } catch { setMessages([]); }
  };
  // Small helper because `l` may not be set when loadMessages is scheduled.
  const isOwnerCheck = () => user && l && user.user_id === l.owner_id;
  useEffect(() => {
    // Skip loading chat entirely for reusable listings (no chat exists).
    if (user && l && l.type !== "reusable") loadMessages(activePeerId);
    /* eslint-disable-next-line */
  }, [user, l, activePeerId]);

  // Lotto 12 — auto-open edit dialog if URL has ?edit=1 (from MyListings link)
  useEffect(() => {
    if (!l || !user) return;
    if (user.user_id !== l.owner_id) return;
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("edit") === "1") {
        setEditForm({
          title: l.title || "",
          description: l.description || "",
          price: l.price != null ? String(l.price) : "",
          condition: l.condition || "",
          color: l.color || "",
          keywords: (l.keywords || []).join(", "),
          pickup: l.pickup !== false,
          shipping: !!l.shipping,
          negotiable: !!l.negotiable,
        });
        setEditOpen(true);
      }
    } catch { /* noop */ }
  }, [l, user]);

  if (!l) return <div className="min-h-screen bg-[#F7F5F0]"><AppHeader /><div className="p-8 text-slate-500">Caricamento…</div></div>;

  const M = TYPE_META[l.type];
  const isOwner = user && user.user_id === l.owner_id;
  const isSelectedBuyer = user && user.user_id === l.selected_buyer_id;
  const isSale = l.type === "sale";
  const isReusable = l.type === "reusable";
  // 14-17: no publish, no chat, no follow, no negotiation. Mirrors backend
  // `_require_adult` gating on the 9 write endpoints so the UI stays honest.
  const isMinor = !!user && user.is_adult === false;

  const sendMsg = async () => {
    if (!text.trim()) return;
    // iter80 gate: only on the FIRST send from an adult who hasn't accepted yet.
    const ok = await requireMarketplaceRules();
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/marketplace/listings/${id}/messages`, { text: text.trim(), peer_id: isOwner ? activePeerId : null });
      setText(""); loadMessages(activePeerId);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  // Lotto 12 — Owner edits listing without duplication.
  const openEditForm = () => {
    setEditForm({
      title: l.title || "",
      description: l.description || "",
      price: l.price != null ? String(l.price) : "",
      condition: l.condition || "",
      color: l.color || "",
      keywords: (l.keywords || []).join(", "),
      pickup: l.pickup !== false,
      shipping: !!l.shipping,
      negotiable: !!l.negotiable,
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    setBusy(true);
    try {
      const patch = {
        title: editForm.title,
        description: editForm.description,
        condition: editForm.condition || undefined,
        color: editForm.color || undefined,
        keywords: editForm.keywords ? editForm.keywords.split(/\s*,\s*/).filter(Boolean).slice(0, 20) : [],
        pickup: !!editForm.pickup,
      };
      if (l.type === "sale") {
        if (editForm.price !== "") patch.price = parseFloat(editForm.price);
        patch.negotiable = !!editForm.negotiable;
      }
      if (l.type !== "reusable") patch.shipping = !!editForm.shipping;
      const { data } = await api.patch(`/marketplace/listings/${id}`, patch);
      setL(data);
      setEditOpen(false);
      toast.success(t("mk_updated") || "Annuncio aggiornato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error") || "Errore");
    } finally { setBusy(false); }
  };
  const ownerDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/marketplace/listings/${id}`);
      toast.success(t("mk_deleted") || "Annuncio eliminato");
      nav("/me/listings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error") || "Errore");
    } finally { setBusy(false); }
  };

  const selectBuyer = async (peerId) => {
    setBusy(true);
    try {
      await api.post(`/marketplace/listings/${id}/select-buyer`, { buyer_id: peerId });
      toast.success("Acquirente selezionato");
      setSelectBuyerOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const doAction = async (endpoint, msg) => {
    setBusy(true);
    try {
      await api.post(`/marketplace/listings/${id}/${endpoint}`, {});
      toast.success(msg); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const toggleFollow = async () => {
    if (!user) { nav("/auth?mode=signin"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/marketplace/listings/${id}/follow`, {});
      toast.success(data.following ? "Ora segui questo annuncio" : "Hai smesso di seguirlo");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="mk-detail-page">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-6 pb-4"><BackButton /></div>
      <div className="max-w-5xl mx-auto px-4 md:px-8 pb-8 space-y-4">
        <div className={`bg-white border-2 ${M.ring} rounded-2xl overflow-hidden shadow-sm`}>
          <div className="p-5 md:p-6">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Badge style={{ background: M.color, color: '#fff' }}>{t(`mk_type_${l.type}`)}</Badge>
              <Badge variant="outline">{l.category} → {l.subcategory} → {l.item_type}</Badge>
              {l.state === "pending_confirmation" && <Badge className="bg-amber-100 text-amber-800"><Clock size={11} className="mr-1" /> In attesa di conferma</Badge>}
              {l.state === "closed" && <Badge className="bg-slate-200 text-slate-700">Chiuso</Badge>}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900" data-testid="mk-detail-title">{l.title}</h1>
            {isSale && <div className="mt-1 text-xl font-bold text-purple-700">€ {l.price} {l.negotiable && <span className="text-xs font-normal text-purple-500">(trattabile)</span>}</div>}
            {user && !isOwner && !isMinor && (
              <div className="mt-3">
                <Button
                  data-testid="mk-follow-btn"
                  size="sm"
                  variant={l.is_following ? "default" : "outline"}
                  onClick={toggleFollow}
                  disabled={busy}
                  className={`rounded-full ${l.is_following ? "bg-emerald-700 hover:bg-red-600 text-white hover:text-white group" : "border-emerald-300 text-emerald-800 hover:bg-emerald-50 hover:text-emerald-900"}`}
                >
                  {l.is_following ? (
                    <>
                      <BellOff size={13} className="mr-1 hidden group-hover:inline" />
                      <Bell size={13} className="mr-1 inline group-hover:hidden" />
                      <span className="hidden group-hover:inline">Smetti di seguire</span>
                      <span className="inline group-hover:hidden">Seguito</span>
                    </>
                  ) : (
                    <><Bell size={13} className="mr-1" /> Segui</>
                  )}
                </Button>
                {typeof l.followers_count === "number" && l.followers_count > 0 && (
                  <span className="ml-2 text-xs text-slate-500">{l.followers_count} {l.followers_count === 1 ? "persona segue" : "persone seguono"}</span>
                )}
              </div>
            )}
            {user && !isOwner && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="mk-content-tools">
                <ReportContentButton
                  targetType="marketplace_listing"
                  targetId={l.listing_id}
                  testid="mk-report-listing"
                />
                <BlockUserButton
                  targetUserId={l.owner_id}
                  testid="mk-block-owner"
                />
              </div>
            )}
            {isMinor && !isOwner && (
              <div
                data-testid="mk-detail-adult-only-banner"
                className="mt-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2"
              >
                {t("mk_adult_only_banner")}
              </div>
            )}
            <p className="mt-3 text-sm text-slate-700 whitespace-pre-line">{l.description}</p>
            <div className="mt-3 text-xs text-slate-500 flex flex-wrap gap-3">
              <span><MapPin size={11} className="inline mr-1" />{l.province_name} · {l.province_country_code}</span>
              {l.color && <span>Colore: {l.color}</span>}
              {l.condition && <span>Condizione: {l.condition}</span>}
              {l.keywords?.length > 0 && <span>Keyword: {l.keywords.join(", ")}</span>}
              {isSale && <span>{l.pickup ? "Ritiro" : ""}{l.pickup && l.shipping ? " + " : ""}{l.shipping ? "Spedizione" : ""}</span>}
            </div>
            {/* Phase 5 — share only for published listings */}
            {l.state === "published" && (
              <div className="mt-3">
                <ShareButton
                  path={`/marketplace/${id}`}
                  title={l.title}
                  description={(l.description || "").slice(0, 200)}
                  testid="mk-share"
                />
              </div>
            )}
          </div>
          {l.photos?.length > 0 && (
            <div className="grid grid-cols-2 gap-1">
              {l.photos.slice(0, 4).map((p, i) => (
                <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-40 object-cover" />
              ))}
            </div>
          )}
          {l.videos && l.videos.length > 0 && (
            <div className="p-3 border-t border-slate-100">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{t("video_upload_label")}</div>
              <VideoGallery paths={l.videos} testidPrefix="mk-videos" />
            </div>
          )}
        </div>

        {/* Owner: edit/delete controls for own Vendo/Regalo listings (Lotto 12).
            Reusable listings are informational-only — no chat, but the owner
            can still edit metadata and remove the listing. */}
        {isOwner && l.state !== "closed" && (
          <div className="bg-white border-2 border-slate-200 rounded-2xl p-4" data-testid="mk-owner-actions">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{t("mk_owner_actions") || "I tuoi controlli"}</div>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="mk-edit-btn" onClick={openEditForm} variant="outline" className="rounded-full hover:bg-slate-900 hover:text-white">
                <Pencil size={13} className="mr-1" /> {t("mk_edit_listing") || "Modifica annuncio"}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="mk-delete-btn" variant="outline" className="rounded-full text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800">
                    <Trash2 size={13} className="mr-1" /> {t("mk_delete_listing") || "Elimina annuncio"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="mk-delete-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("mk_delete_listing_title") || "Confermi l'eliminazione?"}</AlertDialogTitle>
                    <AlertDialogDescription>{t("mk_delete_listing_desc") || "L'annuncio verrà rimosso definitivamente dal Marketplace e dalla mappa. L'operazione non è reversibile."}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="mk-delete-cancel">{t("cancel")}</AlertDialogCancel>
                    <AlertDialogAction data-testid="mk-delete-confirm" onClick={ownerDelete} className="bg-red-600 hover:bg-red-700 text-white">
                      {t("delete") || "Elimina"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        )}

        {/* Owner: seller actions (Vendo only) */}
        {isOwner && isSale && l.state === "published" && (
          <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-4" data-testid="mk-owner-panel">
            <div className="text-[10px] uppercase tracking-widest text-purple-800 font-semibold mb-2">Azioni venditore</div>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="mk-select-buyer" onClick={() => setSelectBuyerOpen(true)} variant="outline" className="rounded-full">
                <HandCoins size={13} className="mr-1" /> {l.selected_buyer_id ? "Cambia acquirente" : "Seleziona acquirente"}
              </Button>
              <Button data-testid="mk-mark-sold" onClick={() => doAction("mark-sold", "Vendita in attesa di conferma acquirente")}
                disabled={!l.selected_buyer_id || busy}
                className="rounded-full bg-purple-700 hover:bg-purple-800 text-white hover:text-white disabled:opacity-60">
                <CheckCircle2 size={13} className="mr-1" /> Venduto
              </Button>
            </div>
            {!l.selected_buyer_id && <div className="mt-2 text-xs text-purple-800/80">Prima seleziona l'acquirente dalla chat, poi puoi premere «Venduto».</div>}
          </div>
        )}

        {/* Selected buyer: buyer-side action */}
        {isSelectedBuyer && isSale && l.state === "published" && (
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-4" data-testid="mk-buyer-panel">
            <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold mb-2">Sei stato selezionato come acquirente</div>
            <Button data-testid="mk-mark-picked" onClick={() => doAction("mark-picked-up", "Ritiro in attesa di conferma venditore")}
              className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white hover:text-white">
              <PackageCheck size={13} className="mr-1" /> Oggetto ritirato
            </Button>
          </div>
        )}

        {/* Pending confirmation banner */}
        {l.state === "pending_confirmation" && (isOwner || isSelectedBuyer) && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4" data-testid="mk-pending-panel">
            <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold mb-1">
              {l.pending_direction === "seller_initiated" ? "Il venditore ha dichiarato «Venduto»" : "L'acquirente ha dichiarato «Oggetto ritirato»"}
            </div>
            <div className="text-sm text-amber-900 mb-3">L'annuncio è temporaneamente nascosto ai visitatori pubblici.</div>
            {((l.pending_direction === "seller_initiated" && isSelectedBuyer) || (l.pending_direction === "buyer_initiated" && isOwner)) ? (
              <div className="flex gap-2">
                <Button data-testid="mk-confirm" onClick={() => doAction("confirm", "Vendita conclusa")} disabled={busy} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white hover:text-white">
                  <CheckCircle2 size={13} className="mr-1" /> Conferma
                </Button>
                <Button data-testid="mk-refuse" onClick={() => doAction("refuse", "Rifiutato — l'annuncio è tornato pubblico")} disabled={busy} variant="outline" className="rounded-full border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800">
                  <XCircle size={13} className="mr-1" /> Rifiuta
                </Button>
              </div>
            ) : (
              <div className="text-xs text-amber-900/80 italic">{t("awaiting_counterpart")}</div>
            )}
          </div>
        )}

        {/* Chat / info — hidden for reusable listings (informational only, no chat)
            and hidden for 14-17 minors (marketplace read-only). */}
        {user && !isReusable && !isMinor && (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="mk-chat">
            <div className="p-3 border-b border-slate-100 flex items-center gap-3">
              {l.photos && l.photos[0] && (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${l.photos[0]}`} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
                  <MessageCircle size={11} /> Chat privata
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                  <Badge style={{ background: M.color, color: '#fff' }} className="text-[10px]">{t(`mk_type_${l.type}`)}</Badge>
                  <a href={`/marketplace/${id}`} data-testid="mk-chat-link-listing" className="text-sm font-semibold text-slate-900 hover:underline truncate">
                    {l.title}
                  </a>
                </div>
              </div>
            </div>

            {/* Non-owner: gate the chat behind an explicit contact button.
                Uses the marketplace 1:1 chat backend (listing_messages) so the
                owner sees this thread directly in their listing page and via
                "I miei annunci". */}
            {!isOwner && !chatOpen && messages.length === 0 && (
              l.state === "closed" ? (
                <div className="p-4 text-sm text-slate-600 bg-slate-50" data-testid="mk-chat-closed">
                  Annuncio concluso: non è più possibile aprire nuove conversazioni.
                </div>
              ) : (
                <div className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-2">
                  <div className="text-xs text-slate-600 flex-1">
                    Apri una conversazione privata con il proprietario di questo annuncio. Non condividere dati personali (email, telefono, indirizzo) al di fuori della piattaforma.
                  </div>
                  <Button
                    data-testid="mk-contact-btn"
                    disabled={isMinor}
                    onClick={async () => {
                      const ok = await requireMarketplaceRules();
                      if (!ok) return;
                      setChatOpen(true);
                    }}
                    className="rounded-full text-white hover:text-white"
                    style={{ background: M.color }}
                  >
                    <MessageCircle size={13} className="mr-1" />
                    {isSale ? "Contatta il venditore" : "Contatta l'utente"}
                  </Button>
                </div>
              )
            )}

            {/* Owner: existing thread pills */}
            {isOwner && threads.length > 0 && (
              <div className="p-2 border-b border-slate-100 flex gap-2 overflow-x-auto">
                {threads.map((p) => {
                  const lu = mkUnreadPerListing.find((x) => x.listing_id === id);
                  const peerRow = lu?.peers?.find((pp) => pp.peer_id === p.user_id);
                  const un = peerRow?.count || 0;
                  return (
                    <button key={p.user_id} data-testid={`mk-thread-${p.user_id}`} onClick={() => setActivePeerId(p.user_id)} className={`relative px-3 h-8 rounded-full text-xs whitespace-nowrap ${activePeerId === p.user_id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>
                      {p.name || p.user_id}
                      {l.selected_buyer_id === p.user_id && <span className="ml-1 text-emerald-300">✓</span>}
                      {un > 0 && (
                        <span data-testid={`mk-thread-badge-${p.user_id}`} className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
                          {un > 99 ? "99+" : un}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {/* Messages + composer — shown when the chat is open (owner always, interested user once they clicked Contatta). */}
            {(isOwner || chatOpen || messages.length > 0) && (
              <>
                <div className="max-h-[300px] overflow-y-auto p-3 space-y-2" data-testid="mk-messages">
                  {messages.length === 0 && <div className="text-xs text-slate-500 italic">{t("no_messages_write_first")}</div>}
                  {messages.map((m) => (
                    <div key={m.msg_id} className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.author_id === user.user_id ? "bg-emerald-100 ml-auto" : "bg-slate-100"}`}>
                      <div className="flex items-center gap-2 text-[10px] font-semibold text-slate-600 mb-0.5">
                        <span>{displayAuthorName(m, t)}</span>
                        <span className="text-slate-400 font-normal">{new Date(m.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                      {m.text}
                    </div>
                  ))}
                </div>
                {l.state !== "closed" ? (
                  <div className="p-2 border-t border-slate-100 flex gap-2">
                    <Input data-testid="mk-msg-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendMsg(); }} placeholder="Scrivi un messaggio…" className="rounded-full flex-1" />
                    <Button data-testid="mk-msg-send" onClick={sendMsg} disabled={busy || !text.trim()} className="rounded-full bg-slate-800 hover:bg-slate-900 text-white hover:text-white"><Send size={13} /></Button>
                  </div>
                ) : (
                  <div className="p-3 text-[11px] text-slate-500 border-t border-slate-100 bg-slate-50" data-testid="mk-chat-frozen">
                    Annuncio concluso: la conversazione è in sola lettura.
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {/* Reusable listings: informational-only notice, no chat at all */}
        {isReusable && (
          <>
            <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 text-sm text-amber-900" data-testid="mk-reusable-info">
              <div className="font-semibold mb-1">{t("no_chat_for_listing_msg")}</div>
              Gli oggetti riutilizzabili trovati non prevedono conversazioni private né prenotazioni: chi passa per primo può prendere l'oggetto. Sono visibili solo descrizione, fotografie e posizione indicativa.
            </div>
            {/* Lotto 11 — public presence check "Oggetto presente / non presente" */}
            <FoundCheckBlock listingId={l.listing_id} user={user} requireMarketplaceRules={requireMarketplaceRules} t={t} />
            <ReusableCommentsBlock listingId={l.listing_id} isMinor={isMinor} />
          </>
        )}
      </div>

      {/* Lotto 12 — Owner edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="mk-edit-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>{t("mk_edit_listing") || "Modifica annuncio"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div>
              <label className="text-xs font-semibold text-slate-700">{t("mk_title") || "Titolo"}</label>
              <Input data-testid="mk-edit-title" value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} maxLength={200} className="rounded-xl" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">{t("mk_description") || "Descrizione"}</label>
              <Textarea data-testid="mk-edit-description" value={editForm.description} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} maxLength={2000} rows={4} className="rounded-xl" />
            </div>
            {l && l.type === "sale" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700">{t("mk_price") || "Prezzo (€)"}</label>
                  <Input data-testid="mk-edit-price" type="number" step="0.01" min="0" value={editForm.price} onChange={(e) => setEditForm((f) => ({ ...f, price: e.target.value }))} className="rounded-xl" />
                </div>
                <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 mt-6">
                  <input type="checkbox" data-testid="mk-edit-negotiable" checked={editForm.negotiable} onChange={(e) => setEditForm((f) => ({ ...f, negotiable: e.target.checked }))} className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                  {t("mk_negotiable") || "Prezzo trattabile"}
                </label>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">{t("mk_condition") || "Condizione"}</label>
                <select data-testid="mk-edit-condition" value={editForm.condition} onChange={(e) => setEditForm((f) => ({ ...f, condition: e.target.value }))} className="rounded-xl border border-slate-200 h-9 px-2 w-full">
                  <option value="">—</option>
                  <option value="new">{t("mk_cond_new") || "Nuovo"}</option>
                  <option value="like_new">{t("mk_cond_like_new") || "Come nuovo"}</option>
                  <option value="good">{t("mk_cond_good") || "Buone condizioni"}</option>
                  <option value="fair">{t("mk_cond_fair") || "Usato"}</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">{t("mk_color") || "Colore"}</label>
                <Input data-testid="mk-edit-color" value={editForm.color} onChange={(e) => setEditForm((f) => ({ ...f, color: e.target.value }))} maxLength={40} className="rounded-xl" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">{t("mk_keywords") || "Parole chiave"} <span className="text-slate-400">(comma-separated, max 20)</span></label>
              <Input data-testid="mk-edit-keywords" value={editForm.keywords} onChange={(e) => setEditForm((f) => ({ ...f, keywords: e.target.value }))} className="rounded-xl" />
            </div>
            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700">
                <input type="checkbox" data-testid="mk-edit-pickup" checked={editForm.pickup} onChange={(e) => setEditForm((f) => ({ ...f, pickup: e.target.checked }))} className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                {t("mk_pickup") || "Ritiro sul posto"}
              </label>
              {l && l.type !== "reusable" && (
                <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700">
                  <input type="checkbox" data-testid="mk-edit-shipping" checked={editForm.shipping} onChange={(e) => setEditForm((f) => ({ ...f, shipping: e.target.checked }))} className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                  {t("mk_shipping") || "Spedizione disponibile"}
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-full" data-testid="mk-edit-cancel">{t("cancel")}</Button>
            <Button data-testid="mk-edit-save" onClick={saveEdit} disabled={busy || !editForm.title.trim()} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white hover:text-white">
              {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <CheckCircle2 size={13} className="mr-1" />}
              {t("save") || "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Select buyer dialog */}
      <Dialog open={selectBuyerOpen} onOpenChange={setSelectBuyerOpen}>
        <DialogContent data-testid="mk-select-buyer-dialog">
          <DialogHeader><DialogTitle>Seleziona l'acquirente</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-500 mb-2">Scegli dalla chat l'utente con cui hai concluso la vendita. Solo lui potrà partecipare alla conferma.</div>
          <div className="space-y-1 max-h-[240px] overflow-y-auto">
            {threads.length === 0 && <div className="text-sm text-slate-500 italic">Nessun utente ti ha ancora contattato.</div>}
            {threads.map((p) => (
              <button key={p.user_id} onClick={() => selectBuyer(p.user_id)} data-testid={`mk-buyer-${p.user_id}`} className="w-full text-left px-3 py-2 rounded-xl border border-slate-200 hover:bg-emerald-50 text-sm">
                {p.name || p.user_id} <span className="text-xs text-slate-500">{p.user_id}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {gateElement}
    </div>
  );
}

/**
 * Public comments block dedicated to reusable-listing detail pages.
 * Reuses iter70 like/sort primitives + iter60 content-reports.
 * Rendered only when parent already checked `isReusable`.
 */
function ReusableCommentsBlock({ listingId, isMinor }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [comments, setComments] = React.useState([]);
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [sort, setSort] = React.useState(() => {
    try { return localStorage.getItem("tc_reusable_comment_sort") || "recent"; } catch { return "recent"; }
  });

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/marketplace/listings/${listingId}/comments`, { params: { sort } });
      setComments(data.comments || []);
    } catch { /* silent */ }
  }, [listingId, sort]);

  React.useEffect(() => { load(); const it = setInterval(load, 20000); return () => clearInterval(it); }, [load]);
  React.useEffect(() => { try { localStorage.setItem("tc_reusable_comment_sort", sort); } catch {} }, [sort]);

  const submit = async () => {
    if (!user) { toast.info(t("signin_required") || "Accedi per continuare"); return; }
    const t2 = text.trim();
    if (!t2) return;
    setBusy(true);
    try {
      await api.post(`/marketplace/listings/${listingId}/comments`, { text: t2 });
      setText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("generic_error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 mt-4" data-testid="reusable-comments-block">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="font-heading font-semibold text-slate-900">{t("coordinate") || "Commenti"}</div>
        <div className="ml-auto"><CommentSortSelect value={sort} onChange={setSort} testid="reusable-comment-sort" /></div>
      </div>
      <div className="space-y-2 mb-3">
        {comments.length === 0 && <div className="text-sm text-slate-500 italic">{t("no_comments") || "Nessun commento."}</div>}
        {comments.map((c) => {
          const isOwn = user && user.user_id === c.author_id;
          return (
            <div key={c.comment_id} className="border-b border-slate-100 pb-2 group" data-testid={`reusable-comment-${c.comment_id}`}>
              <div className="text-xs font-semibold text-slate-900 flex items-center gap-2">
                <span>{displayAuthorName(c, t)}</span>
                <span className="text-[10px] text-slate-500 font-normal">{new Date(c.created_at).toLocaleString()}</span>
              </div>
              <div className="text-sm text-slate-700 mt-0.5 whitespace-pre-line">{c.text}</div>
              <div className="mt-1 flex items-center gap-2">
                <CommentLikeButton
                  targetKind="listing_comment"
                  targetId={c.comment_id}
                  initialCount={c.like_count || 0}
                  initialLiked={!!c.liked_by_me}
                  testid={`reusable-comment-like-${c.comment_id}`}
                />
                {!isOwn && user && (
                  <ReportContentButton
                    targetType="listing_comment"
                    targetId={c.comment_id}
                    label=""
                    testid={`reusable-comment-report-${c.comment_id}`}
                    className="h-6 px-2 text-[11px] opacity-0 group-hover:opacity-100"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      {user && !isMinor && (
        <div className="flex gap-2 items-end">
          <Textarea
            data-testid="reusable-comment-input"
            rows={2}
            placeholder={t("comment_placeholder") || "Scrivi un commento..."}
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 2000))}
            className="text-sm rounded-xl"
          />
          <Button data-testid="reusable-comment-submit" onClick={submit} disabled={busy || !text.trim()} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white hover:text-white">
            {t("publish") || "Pubblica"}
          </Button>
        </div>
      )}
    </div>
  );
}


/**
 * Lotto 11 — Block pubblico "Oggetto presente / Oggetto non presente"
 * per gli annunci di tipo `reusable`. Ogni utente autenticato può
 * indicare cosa ha trovato sul posto; nessuna auto-archiviazione.
 * Le conferme sono consultabili dall'Admin nei dettagli.
 */
function FoundCheckBlock({ listingId, user, requireMarketplaceRules, t }) {
  const [counts, setCounts] = React.useState({ present_count: 0, not_present_count: 0, total: 0 });
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [lastAction, setLastAction] = React.useState(null); // present|not_present

  const loadCounts = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/marketplace/listings/${listingId}/found-checks`);
      setCounts({
        present_count: data.present_count || 0,
        not_present_count: data.not_present_count || 0,
        total: data.total || 0,
      });
    } catch (e) { /* silent */ }
  }, [listingId]);

  React.useEffect(() => { loadCounts(); }, [loadCounts]);

  const submit = async (present) => {
    if (!user) { toast.error(t("signin_required") || "Devi accedere per confermare"); return; }
    if (requireMarketplaceRules && !requireMarketplaceRules()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/marketplace/listings/${listingId}/found-check`, {
        present,
        note: note || null,
      });
      setCounts({
        present_count: data.present_count,
        not_present_count: data.not_present_count,
        total: (data.present_count || 0) + (data.not_present_count || 0),
      });
      setLastAction(present ? "present" : "not_present");
      setNote("");
      toast.success(t("mk_found_check_saved") || "Grazie! La tua conferma è stata salvata.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error") || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border-2 border-slate-200 rounded-2xl p-4 mt-4" data-testid="found-check-block">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("mk_found_checks_title") || "Conferme utenti"}</div>
      <div className="text-sm text-slate-700 mb-3">{t("mk_found_intro") || "Sei stato sul posto? Aiuta la community: indica se l'oggetto è ancora lì."}</div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="found-check-present-count">
          {t("mk_found_present") || "Oggetto presente"} · {counts.present_count}
        </Badge>
        <Badge className="bg-red-50 text-red-800 border border-red-200" data-testid="found-check-not-present-count">
          {t("mk_found_not_present") || "Oggetto non presente"} · {counts.not_present_count}
        </Badge>
      </div>
      <Textarea
        data-testid="found-check-note"
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 500))}
        placeholder={t("mk_found_note_optional") || "Nota (opzionale)"}
        className="text-sm rounded-xl mb-3"
        rows={2}
      />
      <div className="flex flex-wrap gap-2">
        <Button
          data-testid="found-check-present-btn"
          onClick={() => submit(true)} disabled={busy}
          className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm">
          <CheckCircle2 size={14} className="mr-1" /> {t("mk_found_present") || "Oggetto presente"}
        </Button>
        <Button
          data-testid="found-check-not-present-btn"
          onClick={() => submit(false)} disabled={busy}
          variant="outline"
          className="rounded-full text-sm hover:bg-red-50 hover:text-red-800 border-red-200 text-red-700">
          <XCircle size={14} className="mr-1" /> {t("mk_found_not_present") || "Oggetto non presente"}
        </Button>
      </div>
      {lastAction && (
        <div className="mt-2 text-[11px] text-slate-500 italic">
          {t("mk_found_check_saved") || "Grazie! La tua conferma è stata salvata."}
        </div>
      )}
    </div>
  );
}
